import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    // Return default functions if context is not available
    return {
      permissions: [],
      hasPermission: () => false,
      hasModule: () => false,
      loading: false
    };
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { user, dashboardUser, getToken } = useAuth();
  const currentUser = dashboardUser || user;
  const [permissions, setPermissions] = useState([]);
  const [moduleAccess, setModuleAccess] = useState({});
  const [loading, setLoading] = useState(true);

  const isAdminLike = (currentUser?.role || '').toLowerCase().includes('admin');

  const fetchPermissions = useCallback(async () => {
    try {
      // Admin has all permissions
      if (isAdminLike) {
        setPermissions(['*']); // Wildcard means all permissions
        return;
      }

      // Fetch employee permissions
      const token = getToken(true);
      if (!token || !currentUser?.id) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      const response = await fetch(`/api/admin/employees/${currentUser.id}/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Store permission names for quick lookup
          // Handle both flat array and grouped object
          const permissionsList = Array.isArray(data.data) ? data.data : (data.data.flat || []);
          const permissionNames = permissionsList.map(p => p.name || p);
          setPermissions(permissionNames);
        } else {
          setPermissions([]);
        }
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, getToken, isAdminLike]);

  const fetchModuleAccess = useCallback(async () => {
    try {
      if (!currentUser?.id) {
        setModuleAccess({});
        return;
      }
      const token = getToken(true);
      const response = await fetch(`/api/admin/employees/${currentUser.id}/module-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        setModuleAccess({});
        return;
      }
      const data = await response.json();
      if (data.success) {
        setModuleAccess(data.data?.effective || {});
      } else {
        setModuleAccess({});
      }
    } catch (error) {
      console.error('Error fetching module access:', error);
      setModuleAccess({});
    }
  }, [currentUser?.id, getToken]);

  useEffect(() => {
    if (currentUser && currentUser.role !== 'customer') {
      fetchPermissions();
      fetchModuleAccess();
    } else {
      setPermissions([]);
      setModuleAccess({});
      setLoading(false);
    }
  }, [currentUser, fetchModuleAccess, fetchPermissions]);

  const hasPermission = (permissionName) => {
    if (!permissionName) return true;
    
    // Admin has all permissions
    if (isAdminLike) return true;
    
    // Check if user has wildcard (all permissions)
    if (permissions.includes('*')) return true;
    
    // Check if user has the specific permission
    return permissions.includes(permissionName);
  };

  const hasAnyPermission = (permissionNames = []) => {
    if (!Array.isArray(permissionNames) || permissionNames.length === 0) return true;
    return permissionNames.some((perm) => hasPermission(perm));
  };

  const hasModule = (moduleName) => {
    if (!moduleName) return true;
    
    // Admin has access to all modules
    if (isAdminLike) return true;

    // Respect explicit module access overrides if present
    if (Object.prototype.hasOwnProperty.call(moduleAccess, moduleName)) {
      return !!moduleAccess[moduleName];
    }
    
    // Check if user has any permission in this module
    return permissions.some(perm => {
      // Permission names are like "products.view", "orders.manage", etc.
      return perm.startsWith(`${moduleName}.`);
    });
  };

  const value = {
    permissions,
    hasPermission,
    hasModule,
    hasAnyPermission,
    loading,
    moduleAccess,
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
